import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Users, Lock, Palette, Puzzle, Scale, Database, Settings2, LayoutDashboard, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import AdminOverview from "@/components/admin/AdminOverview";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminRoles from "@/components/admin/AdminRoles";
import AdminFrontpage from "@/components/admin/AdminFrontpage";
import AdminModules from "@/components/admin/AdminModules";
import AdminRGPD from "@/components/admin/AdminRGPD";
import AdminBackup from "@/components/admin/AdminBackup";
import AdminSystem from "@/components/admin/AdminSystem";
import AdminOnboardingInsightsTab from "@/components/admin/AdminOnboardingInsightsTab";
import AdminFrontendErrorsTab from "@/components/admin/AdminFrontendErrorsTab";

const TABS = [
  { id: "overview", label: "Aperçu", icon: LayoutDashboard, Component: AdminOverview },
  { id: "insights", label: "Insights", icon: Sparkles, Component: AdminOnboardingInsightsTab },
  { id: "errors", label: "Erreurs", icon: AlertTriangle, Component: AdminFrontendErrorsTab },
  { id: "users", label: "Utilisateurs", icon: Users, Component: AdminUsers },
  { id: "roles", label: "Rôles", icon: Lock, Component: AdminRoles },
  { id: "frontpage", label: "Frontpage", icon: Palette, Component: AdminFrontpage },
  { id: "modules", label: "Modules", icon: Puzzle, Component: AdminModules },
  { id: "rgpd", label: "RGPD", icon: Scale, Component: AdminRGPD },
  { id: "backup", label: "Backup", icon: Database, Component: AdminBackup },
  { id: "system", label: "Système", icon: Settings2, Component: AdminSystem },
];

export default function Admin() {
  const { user } = useAuth();
  const [authorized, setAuthorized] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setAuthorized(!!data);
      } catch {
        setAuthorized(false);
      }
    })();
  }, []);

  if (authorized === null) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20 text-zinc-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Vérification des droits…
        </div>
      </AppShell>
    );
  }
  if (!authorized) {
    return (
      <AppShell>
        <div className="px-5 pt-10 text-center">
          <Shield className="w-10 h-10 text-[var(--text)] mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold text-[var(--text)] tracking-tight">Accès refusé</h1>
          <p className="text-sm text-zinc-400 mt-2">Ce panneau est réservé aux super administrateurs.</p>
        </div>
      </AppShell>
    );
  }

  const Current = TABS.find((t) => t.id === tab)?.Component || AdminOverview;

  return (
    <AppShell>
      <div className="px-5 pt-6 pb-2">
        <div className="flex items-center gap-2 hp-label" style={{ color: "#86efac" }}>
          <Shield className="w-3.5 h-3.5" /> Console Prosperr
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[var(--text)] mt-2 tracking-tight leading-[0.95]" data-testid="admin-title">
          Administration
        </h1>
        <p className="text-sm text-zinc-400 mt-2">Connecté en tant que <span className="font-semibold text-[var(--text)]">{user?.email}</span></p>
      </div>

      {/* Segmented nav — Heetch Pro tabs with violet underline */}
      <div className="mt-6 sticky top-0 z-20 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)]">
        <div className="px-5 flex gap-0 overflow-x-auto kanban-scroll" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative shrink-0 flex items-center gap-1.5 px-3 h-12 text-sm font-medium transition-colors ${
                  active ? "text-[var(--text)]" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.8} />
                {t.label}
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 pt-5 pb-8" key={tab}>
        <Current />
      </div>
    </AppShell>
  );
}

export { toast };
